// Gli obiettivi del mese del Report (piano M7, Task 6; D9; SEZ-2, SEZ-16; mock-up `goalsSection` del 17/10/2026, come il foglio
// «Calcolo Sales»). Dall'obiettivo di vendita in euro si risale, con il minimo di ogni percentuale, a ciò che serve nel mese:
// obiettivo ÷ valore medio di una vendita = chiusure; ÷ close rate = demo effettuate; ÷ show up delle demo = demo da fare; ÷ demo su
// disco = disco effettuate; ÷ show up delle disco = disco da fare; ÷ disco su chiamate = chiamate; ÷ giorni lavorativi del mese =
// chiamate al giorno. I numeri restano esatti (4,5 chiusure): li arrotonda soltanto chi li scrive.
//
// Il ritmo è quello del mese fino ad adesso: fatto ÷ giorni lavorativi trascorsi × giorni lavorativi del mese. Un giorno lavorativo è
// un giorno aperto del calendario dell'azienda (`workingWindowOf`, chiusure comprese); oggi conta per la parte già lavorata, in minuti
// delle mezze giornate della sequenza (`halfDaysOf`), la pausa fra mattina e pomeriggio esclusa. «In linea» se a questo ritmo si
// arriva all'obiettivo, «poco sotto» dal 90%, «indietro» sotto; prima del primo minuto di lavoro del mese nessuno è indietro. Il mese
// è quello di calendario che contiene oggi nel fuso dell'azienda, qualunque sia il periodo scelto nel Report. Niente testi: le parole
// sono nei cataloghi.
import { halfDaysOf } from "./call-sequence-calendar.js";
import { workingWindowOf } from "./callback-scheduling.js";
import { addLocalDays, dayString, localDateOf, padNumber, parseDayString, requireInstant, requireTimeZone } from "./local-days.js";
/** I passi della risalita, nell'ordine del mock-up: dal valore delle vendite alle chiamate. */
export const GOAL_STEP_IDS = Object.freeze(["value", "closings", "demoHeld", "demoDue", "discoHeld", "discoDue", "calls"]);
/** Come va un passo a questo ritmo: «in linea» dal 100%, «poco sotto» dal 90%, «indietro» sotto. */
export const GOAL_STATUSES = Object.freeze(["inLine", "slightlyBehind", "behind"]);
/** Il conteggio fatto di ogni passo. */
const DONE_OF = Object.freeze({
    value: "valueCents",
    closings: "closings",
    demoHeld: "demoHeld",
    demoDue: "demoDue",
    discoHeld: "discoHeld",
    discoDue: "discoDue",
    calls: "calls",
});
function requireCount(value, label) {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0)
        throw new RangeError(`${label} must be a finite number not below zero`);
    return value;
}
/** Il minimo di una percentuale della risalita: un divisore, quindi più di zero. */
function rateOf(targets, key) {
    const rate = targets?.[key]?.min;
    if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0 || rate > 100)
        throw new RangeError(`${key} must be above 0 and at most 100`);
    return rate;
}
/** `part` diviso per `rate`%: moltiplicato prima, così 4,5 ÷ 20% fa 22,5 esatto. */
const byRate = (part, rate) => (part * 100) / rate;
/**
 * Lo stato a questo ritmo, senza divisioni: in linea se `projected` arriva a `needed`, poco sotto dal 90%, indietro sotto. Prima del
 * primo minuto di lavoro del mese nessuno è indietro.
 */
function statusOf(needed, projected, started) {
    if (!started || projected >= needed)
        return "inLine";
    return projected * 10 >= needed * 9 ? "slightlyBehind" : "behind";
}
/** La risalita dall'obiettivo alle chiamate e il ritmo del mese fino ad adesso (vedi in cima al file). */
export function goalPlanOf(input) {
    const salesGoal = requireCount(input.salesGoalCents, "salesGoalCents");
    const averageSale = requireCount(input.averageSaleCents, "averageSaleCents");
    if (averageSale < 1)
        throw new RangeError("averageSaleCents must be at least 1");
    const workingDays = requireCount(input.workingDays, "workingDays");
    const elapsed = requireCount(input.elapsedWorkingDays, "elapsedWorkingDays");
    if (elapsed > workingDays)
        throw new RangeError("elapsedWorkingDays must not exceed workingDays");
    const { targets } = input;
    const needed = {};
    needed.value = salesGoal;
    needed.closings = salesGoal / averageSale;
    needed.demoHeld = byRate(needed.closings, rateOf(targets, "closeRate"));
    needed.demoDue = byRate(needed.demoHeld, rateOf(targets, "demoShow"));
    needed.discoHeld = byRate(needed.demoDue, rateOf(targets, "demoOnDisco"));
    needed.discoDue = byRate(needed.discoHeld, rateOf(targets, "discoShow"));
    needed.calls = byRate(needed.discoDue, rateOf(targets, "discoOnCalls"));
    const started = elapsed > 0;
    const expectedShare = workingDays > 0 ? elapsed / workingDays : 0;
    const steps = GOAL_STEP_IDS.map((id) => {
        const done = requireCount(input.done[DONE_OF[id]], DONE_OF[id]);
        const projected = started ? (done * workingDays) / elapsed : done;
        return {
            id,
            needed: needed[id],
            done,
            projected,
            status: statusOf(needed[id], projected, started),
            doneShare: needed[id] > 0 ? done / needed[id] : 1,
            expectedShare,
        };
    });
    return {
        steps,
        callsPerDayNeeded: workingDays > 0 ? needed.calls / workingDays : 0,
        // La media al giorno lavorativo trascorso, almeno uno come il mock-up: la prima mattina del mese non moltiplica le chiamate.
        callsPerDayAverage: input.done.calls / Math.max(1, elapsed),
    };
}
const MONTH = /^(\d{4})-(\d{2})$/;
/** Il primo e l'ultimo giorno del mese `AAAA-MM`; una forma sbagliata o un mese che non c'è è un `RangeError`. */
export function monthBoundsOf(month) {
    const match = typeof month === "string" ? MONTH.exec(month) : null;
    const year = Number(match?.[1]);
    const number = Number(match?.[2]);
    if (!match || number < 1 || number > 12)
        throw new RangeError("month must be a calendar month (YYYY-MM)");
    const first = { year, month: number, day: 1 };
    const last = addLocalDays({ year: number === 12 ? year + 1 : year, month: number === 12 ? 1 : number + 1, day: 1 }, -1);
    return { month, from: dayString(first), to: dayString(last) };
}
/** Il mese `delta` mesi dopo `month` (prima, se `delta` è negativo). */
export function shiftMonth(month, delta) {
    if (!Number.isInteger(delta))
        throw new RangeError("delta must be an integer");
    const { from } = monthBoundsOf(month);
    const { year, month: number } = parseDayString(from);
    const index = year * 12 + (number - 1) + delta;
    return `${padNumber(Math.floor(index / 12), 4)}-${padNumber((index % 12) + 1)}`;
}
/** Il mese di calendario che contiene `now` nel fuso dell'azienda. */
export function monthOf(now, timeZone) {
    const zone = requireTimeZone(timeZone);
    const today = localDateOf(requireInstant(now, "now"), zone);
    return monthBoundsOf(`${padNumber(today.year, 4)}-${padNumber(today.month)}`);
}
const MINUTE_MS = 60_000;
/** La parte di un giorno lavorativo già lavorata a `now`: minuti delle mezze giornate fino ad adesso ÷ minuti delle mezze giornate. */
function workedShareOf(day, now, calendar, rule, close) {
    // I minuti dell'orologio: secondi e millisecondi non contano, come per le mezze giornate.
    const minute = Math.floor(now / MINUTE_MS) * MINUTE_MS;
    let total = 0;
    let worked = 0;
    for (const half of halfDaysOf(day, calendar, rule)) {
        const start = half.start.getTime();
        const end = half.end.getTime();
        total += end - start;
        worked += Math.max(0, Math.min(minute, end) - start);
    }
    // Un giorno aperto senza mezze giornate (la sequenza non chiama in quelle ore) è lavorato intero alla chiusura.
    if (total === 0)
        return minute >= close.getTime() ? 1 : 0;
    return worked / total;
}
/**
 * I giorni lavorativi del mese e quelli trascorsi a `now`: i giorni lavorativi prima di oggi più la parte di oggi già lavorata
 * (minuti delle mezze giornate, pausa esclusa). Un mese passato è trascorso tutto, uno che deve venire non è cominciato.
 * `workingDaysSoFar` sono i giorni lavorativi fino a oggi compreso, a qualunque ora (il «giorno lavorativo {n} di {m}» del mock-up,
 * `doneDays`): giovedì alle 8, prima dell'apertura, è già il suo giorno; un giorno chiuso tiene l'ultimo lavorativo.
 */
export function elapsedWorkingDaysOf(month, now, calendar, rule) {
    const instant = requireInstant(now, "now");
    const today = dayString(localDateOf(instant, requireTimeZone(calendar?.timeZone)));
    let workingDays = 0;
    let elapsedWorkingDays = 0;
    let workingDaysSoFar = 0;
    const last = parseDayString(month.to);
    for (let date = parseDayString(month.from); dayString(date) <= dayString(last); date = addLocalDays(date, 1)) {
        const window = workingWindowOf(date, calendar);
        if (!window)
            continue;
        workingDays += 1;
        const day = dayString(date);
        if (day <= today)
            workingDaysSoFar += 1;
        if (day < today)
            elapsedWorkingDays += 1;
        else if (day === today)
            elapsedWorkingDays += workedShareOf(day, instant, calendar, rule, window.close);
    }
    return { workingDays, elapsedWorkingDays, workingDaysSoFar };
}
//# sourceMappingURL=report-goals.js.map